import { useState, useEffect } from 'react'
import { guestService, agentService, type Guest, type Agent, type GuestAgent } from '../lib/guestService'
import { useEventYear } from '../components/EventYearContext'

export function useGuests() {
  const { currentYear } = useEventYear()
  const [guests, setGuests] = useState<Guest[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load data on mount and when year changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [guestsData, agentsData] = await Promise.all([
          guestService.getAll(currentYear),
          agentService.getAll(currentYear)
        ])

        setGuests(guestsData)
        setAgents(agentsData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load guests')
        console.error('Error loading guests:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [currentYear])

  const reloadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [guestsData, agentsData] = await Promise.all([
        guestService.getAll(currentYear),
        agentService.getAll(currentYear)
      ])

      setGuests(guestsData)
      setAgents(agentsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guests')
      console.error('Error loading guests:', err)
    } finally {
      setLoading(false)
    }
  }

  const addGuest = async (guest: Omit<Guest, 'id'>) => {
    try {
      const newGuest = await guestService.create(guest, currentYear)
      if (newGuest) {
        setGuests(prev => [newGuest, ...prev])
      }
      return newGuest
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add guest')
      throw err
    }
  }

  const bulkAddGuests = async (guestsData: Omit<Guest, 'id'>[], agentsData: Omit<Agent, 'id'>[]) => {
    try {
      const [newGuests, newAgents] = await Promise.all([
        guestService.bulkCreate(guestsData, currentYear),
        agentService.bulkCreate(agentsData, currentYear)
      ])

      setGuests(prev => [...newGuests, ...prev])
      setAgents(prev => [...newAgents, ...prev])

      return { guests: newGuests, agents: newAgents }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk add guests')
      throw err
    }
  }

  const deleteGuest = async (id: string) => {
    try {
      await guestService.delete(id)
      setGuests(prev => prev.filter(guest => guest.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete guest')
      throw err
    }
  }

  const bulkDeleteGuests = async (ids: string[]) => {
    try {
      await guestService.bulkDelete(ids)
      setGuests(prev => prev.filter(guest => !ids.includes(guest.id)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk delete guests')
      throw err
    }
  }

  const updateGuest = async (id: string, updates: Partial<Omit<Guest, 'id'>>) => {
    try {
      console.log('Updating guest:', id, 'with:', updates)
      const updatedGuest = await guestService.update(id, updates)
      console.log('Updated guest result:', updatedGuest)
      if (updatedGuest) {
        setGuests(prev => prev.map(guest =>
          guest.id === id ? updatedGuest : guest
        ))
      }
      return updatedGuest
    } catch (err) {
      console.error('Error in updateGuest hook:', err)
      setError(err instanceof Error ? err.message : 'Failed to update guest')
      throw err
    }
  }

  const deleteAllGuests = async () => {
    try {
      await guestService.deleteAll()
      setGuests([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete all guests')
      throw err
    }
  }

  const deleteAllAgents = async () => {
    try {
      console.log('useGuests deleteAllAgents called')
      await agentService.deleteAll()
      setAgents([])
      console.log('All agents deleted successfully')
    } catch (err) {
      console.error('Error in deleteAllAgents hook:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete all agents')
      throw err
    }
  }

  const addAgent = async (agent: Omit<Agent, 'id'>) => {
    try {
      const newAgent = await agentService.create(agent, currentYear)
      if (newAgent) {
        setAgents(prev => [newAgent, ...prev])
      }
      return newAgent
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add agent')
      throw err
    }
  }

  const assignAgentToGuest = async (guestId: string, agentId: string, isPrimary: boolean) => {
    try {
      await guestService.assignAgent(guestId, agentId, isPrimary, currentYear)
      await reloadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign agent')
      throw err
    }
  }

  const removeAgentFromGuest = async (guestId: string, agentId: string) => {
    try {
      await guestService.removeAgent(guestId, agentId, currentYear)
      await reloadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove agent')
      throw err
    }
  }

  const setGuestAgents = async (guestId: string, agentAssignments: { agentId: string, isPrimary: boolean }[]) => {
    try {
      await guestService.setAgents(guestId, agentAssignments, currentYear)
      // Re-fetch the updated guest
      const updatedGuest = await guestService.update(guestId, {})
      if (updatedGuest) {
        setGuests(prev => prev.map(g => g.id === guestId ? updatedGuest : g))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update guest agents')
      throw err
    }
  }

  const updateAgent = async (id: string, updates: Partial<Omit<Agent, 'id'>>) => {
    try {
      const updatedAgent = await agentService.update(id, updates)
      if (updatedAgent) {
        setAgents(prev => prev.map(a => a.id === id ? updatedAgent : a))
      }
      return updatedAgent
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent')
      throw err
    }
  }

  const deleteAgent = async (id: string) => {
    try {
      console.log('useGuests deleteAgent called with ID:', id)
      const result = await agentService.delete(id)
      console.log('agentService.delete result:', result)
      setAgents(prev => {
        const filtered = prev.filter(agent => agent.id !== id)
        return filtered
      })
    } catch (err) {
      console.error('Error in deleteAgent hook:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete agent')
      throw err
    }
  }

  return {
    guests,
    agents,
    loading,
    error,
    reloadData,
    addGuest,
    updateGuest,
    bulkAddGuests,
    deleteGuest,
    bulkDeleteGuests,
    deleteAllGuests,
    addAgent,
    assignAgentToGuest,
    removeAgentFromGuest,
    setGuestAgents,
    updateAgent,
    deleteAgent,
    deleteAllAgents
  }
}
